import { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'react-hot-toast';
import { 
  Plus, 
  Trash2, 
  Search, 
  RefreshCcw,
  ExternalLink,
  MessageSquare,
  AlertCircle,
  Share2
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

const createTemplateSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório').regex(/^[a-z0-9_]+$/, 'Nome deve conter apenas letras minúsculas, números e sublinhados'),
  category: z.enum(['MARKETING', 'UTILITY', 'AUTHENTICATION']),
  language: z.string().min(2, 'Idioma é obrigatório'),
  body: z.string().min(1, 'Corpo da mensagem é obrigatório'),
});

type CreateTemplateFormData = z.infer<typeof createTemplateSchema>;

interface MetaTemplatesManagerProps {
  tenantId?: string;
  isSuperAdmin?: boolean;
}

interface MetaTemplate {
  id: string;
  name: string;
  category: string;
  language: string;
  status?: string;
  components?: Array<{
    type: string;
    text?: string;
    buttons?: Array<{
      type: string;
      text?: string;
      url?: string;
    }>;
  }>;
}

interface MetaWaba {
  id: string;
  name: string;
  businessId: string;
}

interface Tenant {
  id: string;
  name: string;
  domain?: string;
}

interface Share {
  id: string;
  tenantId: string;
  tenantName?: string;
  status: string;
}

export function MetaTemplatesManager({ tenantId, isSuperAdmin = false }: MetaTemplatesManagerProps) {
  const [wabas, setWabas] = useState<MetaWaba[]>([]);
  const [selectedWaba, setSelectedWaba] = useState<MetaWaba | null>(null);
  const [templates, setTemplates] = useState<MetaTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isSharingModalOpen, setIsSharingModalOpen] = useState(false);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [shares, setShares] = useState<Share[]>([]);
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    type: 'danger' | 'info';
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => { },
    type: 'info'
  });

  const showConfirm = (title: string, message: string, onConfirm: () => void, type: 'danger' | 'info' = 'info') => {
    setConfirmDialog({
      isOpen: true,
      title,
      message,
      onConfirm,
      type
    });
  };

  const authenticatedFetch = useCallback(async (url: string, options: RequestInit = {}) => {
    const token = localStorage.getItem('auth_token');
    const selectedTenantId = tenantId || localStorage.getItem('selected_tenant_id');
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      (headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }

    if (selectedTenantId) {
      (headers as Record<string, string>)['X-Tenant-Id'] = selectedTenantId;
    }

    return fetch(url, {
      ...options,
      headers,
    });
  }, [tenantId]);

  const loadWabas = useCallback(async () => {
    setLoading(true);
    try {
      const response = await authenticatedFetch('/api/meta/wabas');
      if (response.ok) {
        const data = await response.json();
        // Se for SuperAdmin, o backend retorna { owned, client, all }, senão retorna lista flat
        const wabaList = Array.isArray(data) ? data : (data.all || []);
        setWabas(wabaList);
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao carregar WABAs');
      }
    } catch (error) {
      console.error('Erro ao carregar WABAs:', error);
      toast.error('Erro ao carregar WABAs');
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  const loadTemplates = useCallback(async (wabaId: string) => {
    setLoadingTemplates(true);
    try {
      const response = await authenticatedFetch(`/api/meta/wabas/${wabaId}/templates`);
      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao carregar templates');
      }
    } catch (error) {
      console.error('Erro ao carregar templates:', error);
      toast.error('Erro ao carregar templates');
    } finally {
      setLoadingTemplates(false);
    }
  }, [authenticatedFetch]);

  const loadTenants = useCallback(async () => {
    if (!isSuperAdmin) return;
    setLoadingTenants(true);
    try {
      // Usar endpoint mais leve que retorna apenas dados essenciais dos tenants
      const response = await authenticatedFetch('/api/users/tenants');
      if (response.ok) {
        const data = await response.json();
        // O endpoint /api/users/tenants retorna { success: true, data: { tenants: [...] } }
        setTenants(data.data?.tenants || []);
      }
    } catch (error) {
      console.error('Erro ao carregar tenants:', error);
    } finally {
      setLoadingTenants(false);
    }
  }, [authenticatedFetch, isSuperAdmin]);

  const loadShares = useCallback(async (wabaId: string) => {
    if (!isSuperAdmin) return;
    setLoadingShares(true);
    try {
      const response = await authenticatedFetch(`/api/meta/wabas/${wabaId}/shares`);
      if (response.ok) {
        const data = await response.json();
        setShares(data);
      }
    } catch (error) {
      console.error('Erro ao carregar compartilhamentos:', error);
    } finally {
      setLoadingShares(false);
    }
  }, [authenticatedFetch, isSuperAdmin]);

  const handleShare = async (tenantId: string) => {
    if (!selectedWaba) return;
    try {
      const response = await authenticatedFetch(`/api/meta/wabas/${selectedWaba.id}/share`, {
        method: 'POST',
        body: JSON.stringify({ tenantId })
      });
      if (response.ok) {
        toast.success('WABA compartilhada com sucesso');
        loadShares(selectedWaba.id);
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao compartilhar WABA');
      }
    } catch (error) {
      console.error('Erro ao compartilhar WABA:', error);
      toast.error('Erro ao compartilhar WABA');
    }
  };

  const handleUnshare = async (tenantId: string) => {
    if (!selectedWaba) return;
    try {
      const response = await authenticatedFetch(`/api/meta/wabas/${selectedWaba.id}/share/${tenantId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        toast.success('Compartilhamento removido');
        loadShares(selectedWaba.id);
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao remover compartilhamento');
      }
    } catch (error) {
      console.error('Erro ao remover compartilhamento:', error);
      toast.error('Erro ao remover compartilhamento');
    }
  };

  useEffect(() => {
    loadWabas();
    if (isSuperAdmin) {
      loadTenants();
    }
  }, [loadWabas, loadTenants, isSuperAdmin]);

  useEffect(() => {
    if (selectedWaba) {
      loadTemplates(selectedWaba.id);
      if (isSuperAdmin) {
        loadShares(selectedWaba.id);
      }
    } else {
      setTemplates([]);
      setShares([]);
    }
  }, [selectedWaba, loadTemplates, loadShares, isSuperAdmin]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<CreateTemplateFormData>({
    resolver: zodResolver(createTemplateSchema),
    defaultValues: {
      category: 'MARKETING',
      language: 'pt_BR'
    }
  });

  const onCreateSubmit = async (data: CreateTemplateFormData) => {
    if (!selectedWaba) return;

    try {
      const payload = {
        name: data.name,
        category: data.category,
        language: data.language,
        components: [
          {
            type: 'BODY',
            text: data.body
          }
        ]
      };

      const response = await authenticatedFetch(`/api/meta/wabas/${selectedWaba.id}/templates`, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Template criado com sucesso');
        setIsCreateModalOpen(false);
        reset();
        loadTemplates(selectedWaba.id);
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Erro ao criar template');
      }
    } catch (error) {
      console.error('Erro ao criar template:', error);
      toast.error('Erro ao criar template');
    }
  };

  const handleDeleteTemplate = (template: MetaTemplate) => {
    showConfirm(
      'Excluir Template',
      `Tem certeza que deseja excluir o template "${template.name}"? Esta ação não pode ser desfeita na Meta.`,
      async () => {
        try {
          const response = await authenticatedFetch(`/api/meta/wabas/${selectedWaba.id}/templates?name=${template.name}&id=${template.id}`, {
            method: 'DELETE'
          });

          if (response.ok) {
            toast.success('Template excluído com sucesso');
            loadTemplates(selectedWaba.id);
          } else {
            const errorData = await response.json();
            toast.error(errorData.error || 'Erro ao excluir template');
          }
        } catch (error) {
          console.error('Erro ao excluir template:', error);
          toast.error('Erro ao excluir template');
        }
      },
      'danger'
    );
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="w-12 h-12 border-4 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"></div>
        <div className="text-gray-400 font-oswald uppercase tracking-[0.2em] font-bold text-sm">Carregando contas WhatsApp...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 bg-white/5 p-8 rounded-[2rem] border border-white/10 backdrop-blur-md relative overflow-hidden group/header shadow-xl">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-600/5 to-transparent opacity-0 group-hover/header:opacity-100 transition-opacity" />
        <div className="relative z-10">
          <h2 className="text-3xl font-bold font-oswald text-white uppercase tracking-tight flex items-center gap-3">
            <span className="p-2 bg-emerald-500/20 rounded-xl text-emerald-400 border border-emerald-500/30">📝</span>
            Templates Meta
          </h2>
          <p className="text-[10px] font-bold text-gray-400 mt-2 uppercase tracking-[0.2em] font-oswald">Gerencie seus templates do WhatsApp Business diretamente na Meta</p>
        </div>
        
        {selectedWaba && (
          <div className="flex gap-4 relative z-10">
            {isSuperAdmin && (
              <button
                onClick={() => setIsSharingModalOpen(true)}
                className="px-8 py-4 bg-blue-600/20 text-blue-400 rounded-2xl font-bold font-oswald uppercase tracking-[0.2em] text-xs hover:bg-blue-600 hover:text-white shadow-lg shadow-blue-900/20 hover:-translate-y-1 transition-all duration-300 flex items-center gap-3 group/btn overflow-hidden border border-blue-400/50"
              >
                <Share2 className="w-5 h-5 group-hover:scale-110 transition-transform duration-300" /> 
                Compartilhar
              </button>
            )}
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="px-8 py-4 bg-emerald-600 text-white rounded-2xl font-bold font-oswald uppercase tracking-[0.2em] text-xs hover:bg-emerald-500 shadow-lg shadow-emerald-900/20 hover:-translate-y-1 transition-all duration-300 flex items-center gap-3 group/btn overflow-hidden border border-emerald-400/50"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
              <Plus className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" /> 
              Novo Template
            </button>
          </div>
        )}
      </div>

      {/* WABA Selection */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-1 space-y-4">
          <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-oswald ml-2 flex items-center gap-2">
            <RefreshCcw className={`w-3 h-3 ${loading ? 'animate-spin' : 'cursor-pointer'}`} onClick={() => loadWabas()} />
            Contas Disponíveis
          </h3>
          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-4 space-y-2 max-h-[500px] overflow-y-auto custom-scrollbar shadow-lg">
            {wabas.length === 0 ? (
              <div className="p-8 text-center">
                <AlertCircle className="w-8 h-8 text-gray-500 mx-auto mb-3 opacity-50" />
                <p className="text-xs font-bold text-gray-500 uppercase tracking-widest font-oswald">Nenhuma conta encontrada</p>
              </div>
            ) : (
              wabas.map((waba) => (
                <button
                  key={waba.id}
                  onClick={() => setSelectedWaba(waba)}
                  className={`w-full text-left p-4 rounded-2xl transition-all duration-300 group ${
                    selectedWaba?.id === waba.id
                      ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-900/40 border border-emerald-400/20'
                      : 'text-gray-400 hover:bg-white/5 hover:text-white border border-transparent'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-xl transition-colors ${
                      selectedWaba?.id === waba.id ? 'bg-white/20' : 'bg-white/5 group-hover:bg-white/10'
                    }`}>
                      <MessageSquare className="w-4 h-4" />
                    </div>
                    <div className="flex-1 overflow-hidden">
                      <div className="font-bold font-oswald uppercase tracking-wide truncate">{waba.name}</div>
                      <div className={`text-[9px] font-bold uppercase tracking-widest mt-1 opacity-60 ${
                        selectedWaba?.id === waba.id ? 'text-white' : 'text-gray-500'
                      }`}>
                        ID: {waba.id}
                      </div>
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Templates List */}
        <div className="md:col-span-2 space-y-4">
          <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-oswald ml-2 flex items-center gap-2">
            {selectedWaba && <RefreshCcw className={`w-3 h-3 ${loadingTemplates ? 'animate-spin' : 'cursor-pointer'}`} onClick={() => loadTemplates(selectedWaba.id)} />}
            {selectedWaba ? `Templates de ${selectedWaba.name}` : 'Selecione uma conta'}
          </h3>
          
          <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-[2rem] overflow-hidden shadow-2xl relative min-h-[400px]">
            {!selectedWaba ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center p-12 text-center">
                <div className="w-20 h-20 bg-white/5 rounded-3xl flex items-center justify-center mb-6 border border-white/10 shadow-inner group">
                  <Search className="w-10 h-10 text-gray-500 group-hover:scale-110 transition-transform duration-500 opacity-50" />
                </div>
                <h4 className="text-xl font-bold text-white mb-2 font-oswald uppercase tracking-tight">Nenhuma conta selecionada</h4>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] max-w-xs mx-auto">
                  Selecione uma conta WhatsApp Business na lista ao lado para gerenciar seus templates
                </p>
              </div>
            ) : loadingTemplates ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
                <div className="w-10 h-10 border-4 border-emerald-500/20 border-t-emerald-500 rounded-full animate-spin"></div>
                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-oswald">Buscando templates na Meta...</div>
              </div>
            ) : templates.length === 0 ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center p-12 text-center">
                <AlertCircle className="w-12 h-12 text-gray-500 mb-4 opacity-30" />
                <h4 className="text-xl font-bold text-white mb-2 font-oswald uppercase tracking-tight">Nenhum template encontrado</h4>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mb-8">Esta conta ainda não possui templates criados</p>
                <button
                  onClick={() => setIsCreateModalOpen(true)}
                  className="px-8 py-3 bg-emerald-600/20 text-emerald-400 border border-emerald-500/30 rounded-xl font-bold font-oswald uppercase tracking-widest text-[10px] hover:bg-emerald-600 hover:text-white transition-all shadow-lg shadow-emerald-900/20"
                >
                  Criar Primeiro Template
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto custom-scrollbar">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-white/5 border-b border-white/10">
                      <th className="px-8 py-6 text-left text-[10px] font-bold text-gray-300 uppercase tracking-[0.2em] font-oswald">Template</th>
                      <th className="px-8 py-6 text-left text-[10px] font-bold text-gray-300 uppercase tracking-[0.2em] font-oswald">Categoria</th>
                      <th className="px-8 py-6 text-center text-[10px] font-bold text-gray-300 uppercase tracking-[0.2em] font-oswald">Idioma</th>
                      <th className="px-8 py-6 text-center text-[10px] font-bold text-gray-300 uppercase tracking-[0.2em] font-oswald">Status</th>
                      <th className="px-8 py-6 text-center text-[10px] font-bold text-gray-300 uppercase tracking-[0.2em] font-oswald">Ações</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {templates.map((template) => (
                      <tr key={template.id} className="hover:bg-white/[0.02] transition-colors group">
                        <td className="px-8 py-6">
                          <div className="font-bold text-white text-base font-oswald uppercase tracking-wide">{template.name}</div>
                          <div className="text-[9px] font-bold text-gray-500 mt-1 uppercase tracking-widest font-oswald truncate max-w-[200px]">
                            ID: {template.id}
                          </div>
                        </td>
                        <td className="px-8 py-6">
                          <span className={`px-3 py-1 rounded-lg text-[9px] font-bold uppercase tracking-widest border font-oswald ${
                            template.category === 'MARKETING' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' :
                            template.category === 'UTILITY' ? 'bg-amber-500/10 text-amber-400 border-amber-500/20' :
                            'bg-purple-500/10 text-purple-400 border-purple-500/20'
                          }`}>
                            {template.category}
                          </span>
                        </td>
                        <td className="px-8 py-6 text-center">
                          <span className="text-gray-300 font-bold font-oswald text-xs uppercase">{template.language}</span>
                        </td>
                        <td className="px-8 py-6 text-center">
                          <span className={`inline-flex items-center px-4 py-1.5 rounded-full text-[10px] font-bold transition-all uppercase tracking-wider font-oswald border ${
                            template.status === 'APPROVED' ? 'bg-green-500/10 text-green-400 border-green-500/30 shadow-[0_0_10px_rgba(34,197,94,0.1)]' :
                            template.status === 'REJECTED' ? 'bg-red-500/10 text-red-400 border-red-500/30' :
                            'bg-yellow-500/10 text-yellow-400 border-yellow-500/30'
                          }`}>
                            {template.status === 'APPROVED' ? 'Aprovado' : 
                             template.status === 'REJECTED' ? 'Rejeitado' : 
                             template.status === 'PENDING' ? 'Pendente' : template.status}
                          </span>
                        </td>
                        <td className="px-8 py-6">
                          <div className="flex items-center justify-center gap-3">
                            <a 
                              href={`https://business.facebook.com/wa/manage/templates/?business_id=${template.business_id || ''}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="w-10 h-10 flex items-center justify-center text-blue-400 bg-blue-500/5 hover:bg-blue-500/20 rounded-xl transition-all border border-blue-500/20 group/icon"
                              title="Ver na Meta"
                            >
                              <ExternalLink className="w-4 h-4 group-hover/icon:scale-110 transition-transform" />
                            </a>
                            <button
                              onClick={() => handleDeleteTemplate(template)}
                              className="w-10 h-10 flex items-center justify-center text-red-400 bg-red-500/5 hover:bg-red-500/20 rounded-xl transition-all border border-red-500/20 group/icon"
                              title="Excluir Template"
                            >
                              <Trash2 className="w-4 h-4 group-hover/icon:scale-110 transition-transform" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal Criar Template */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-[#0f172a]/95 backdrop-blur-xl rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden border border-white/10 relative z-10 animate-scaleIn">
            <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/10 rounded-full blur-3xl -mr-16 -mt-16" />
            
            <div className="relative px-8 py-7 bg-white/5 border-b border-white/10 flex justify-between items-center overflow-hidden">
              <h3 className="text-xl font-bold font-oswald text-white uppercase tracking-tight flex items-center gap-3">
                <span className="p-2 bg-emerald-500/20 rounded-xl text-emerald-400 border border-emerald-500/30">✨</span>
                Novo Template
              </h3>
              <button 
                onClick={() => setIsCreateModalOpen(false)}
                className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-white/5 rounded-xl"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit(onCreateSubmit)} className="p-8 space-y-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 font-oswald ml-1">Nome do Template</label>
                  <input
                    {...register('name')}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all font-medium placeholder:text-gray-600"
                    placeholder="ex: boas_vindas_v1"
                  />
                  {errors.name && <p className="text-red-400 text-[10px] mt-2 font-bold uppercase tracking-wider ml-1">{errors.name.message}</p>}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 font-oswald ml-1">Categoria</label>
                    <select
                      {...register('category')}
                      className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all font-medium font-oswald uppercase text-xs"
                    >
                      <option value="MARKETING" className="bg-slate-900">MARKETING</option>
                      <option value="UTILITY" className="bg-slate-900">UTILITY</option>
                      <option value="AUTHENTICATION" className="bg-slate-900">AUTHENTICATION</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 font-oswald ml-1">Idioma</label>
                    <input
                      {...register('language')}
                      className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all font-medium placeholder:text-gray-600"
                      placeholder="pt_BR"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2 font-oswald ml-1">Corpo da Mensagem</label>
                  <textarea
                    {...register('body')}
                    className="w-full bg-white/5 border border-white/10 rounded-2xl px-5 py-4 text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 transition-all font-medium min-h-[150px] placeholder:text-gray-600"
                    placeholder="Olá {{1}}, bem-vindo à nossa plataforma!"
                  />
                  <p className="text-[9px] text-gray-500 mt-2 font-bold uppercase tracking-wider ml-1">
                    Use {"{{n}}"} para variáveis (ex: {"{{1}}"}).
                  </p>
                  {errors.body && <p className="text-red-400 text-[10px] mt-2 font-bold uppercase tracking-wider ml-1">{errors.body.message}</p>}
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  className="flex-1 py-4 px-6 border border-white/10 rounded-2xl font-bold font-oswald uppercase tracking-widest text-xs text-gray-400 hover:bg-white/5 hover:text-white transition-all"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 py-4 px-6 bg-emerald-600 text-white rounded-2xl font-bold font-oswald uppercase tracking-widest text-xs hover:bg-emerald-500 shadow-xl shadow-emerald-900/40 hover:-translate-y-1 transition-all disabled:opacity-50 disabled:translate-y-0 group/btn relative overflow-hidden"
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover/btn:animate-shimmer" />
                  {isSubmitting ? 'Criando...' : 'Criar Template'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Compartilhamento */}
      {isSharingModalOpen && selectedWaba && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4 animate-fadeIn">
          <div className="bg-[#0f172a]/95 backdrop-blur-xl rounded-[2.5rem] shadow-2xl w-full max-w-2xl overflow-hidden border border-white/10 relative z-10 animate-scaleIn">
            <div className="relative px-10 py-8 bg-white/5 border-b border-white/10 flex justify-between items-center">
              <div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-6 bg-emerald-500 rounded-full" />
                  <h3 className="text-2xl font-bold font-oswald text-white uppercase tracking-tight">
                    Compartilhar WABA
                  </h3>
                </div>
                <p className="text-[10px] font-bold text-gray-400 mt-1 uppercase tracking-[0.2em] font-oswald ml-5">
                  {selectedWaba.name}
                </p>
              </div>
              <button 
                onClick={() => setIsSharingModalOpen(false)}
                className="text-gray-400 hover:text-white transition-colors p-2 hover:bg-white/5 rounded-xl"
              >
                ✕
              </button>
            </div>

            <div className="p-10 space-y-10">
              {/* Current Shares */}
              <div className="space-y-6">
                <h4 className="text-[10px] font-bold text-emerald-400 uppercase tracking-[0.2em] font-oswald flex items-center gap-2">
                  Tenantes com Acesso
                </h4>
                <div className="space-y-3">
                  {shares.length === 0 ? (
                    <div className="py-8 text-center text-[10px] font-bold text-gray-500 uppercase tracking-widest font-oswald italic opacity-50">
                      Nenhum tenante associado ainda
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-2">
                      {shares.map(share => (
                        <div key={share.id} className="flex items-center justify-between p-4 rounded-2xl bg-white/5 border border-white/10 group">
                          <div>
                            <div className="text-xs font-bold text-white font-oswald uppercase tracking-wide">{share.tenant.name}</div>
                            <div className="text-[9px] text-gray-500 font-bold uppercase tracking-widest">{share.tenant.slug}</div>
                          </div>
                          <button
                            onClick={() => handleUnshare(share.tenantId)}
                            className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                            title="Remover acesso"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Tenants List to Share */}
              <div className="space-y-6">
                <h4 className="text-[10px] font-bold text-emerald-400 uppercase tracking-[0.2em] font-oswald flex items-center gap-2">
                  Adicionar Tenante
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-48 overflow-y-auto custom-scrollbar pr-2">
                  {tenants
                    .filter(t => !shares.some(s => s.tenantId === t.id))
                    .map(tenant => (
                      <button
                        key={tenant.id}
                        onClick={() => handleShare(tenant.id)}
                        className="flex items-center justify-between p-4 rounded-2xl bg-white/5 border border-white/10 hover:border-emerald-500/50 hover:bg-emerald-500/5 transition-all group text-left"
                      >
                        <div>
                          <div className="text-xs font-bold text-white font-oswald uppercase tracking-wide">{tenant.name}</div>
                          <div className="text-[9px] text-gray-500 font-bold uppercase tracking-widest">{tenant.slug}</div>
                        </div>
                        <Plus className="w-4 h-4 text-gray-500 group-hover:text-emerald-400" />
                      </button>
                    ))}
                  {tenants.filter(t => !shares.some(s => s.tenantId === t.id)).length === 0 && (
                    <div className="col-span-2 py-8 text-center text-[10px] font-bold text-gray-500 uppercase tracking-widest font-oswald italic opacity-50">
                      Todos os tenantes já possuem acesso
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="p-10 bg-white/5 border-t border-white/10 flex justify-end">
              <button
                onClick={() => setIsSharingModalOpen(false)}
                className="px-12 py-4 bg-emerald-600 text-white rounded-2xl font-bold font-oswald uppercase tracking-[0.2em] text-xs hover:bg-emerald-500 shadow-lg shadow-emerald-900/20 transition-all hover:-translate-y-1"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      <AlertDialog 
        open={confirmDialog.isOpen} 
        onOpenChange={(open) => setConfirmDialog(prev => ({ ...prev, isOpen: open }))}
      >
        <AlertDialogContent className="bg-slate-900 border border-white/10 rounded-3xl p-8">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-2xl font-bold font-oswald text-white uppercase tracking-tight">
              {confirmDialog.title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400 font-oswald text-sm uppercase tracking-wider leading-relaxed pt-2">
              {confirmDialog.message}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="pt-6 gap-4">
            <AlertDialogCancel className="bg-white/5 border-white/10 text-gray-400 hover:bg-white/10 hover:text-white rounded-2xl px-8 py-6 font-bold font-oswald uppercase tracking-widest text-xs transition-all">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDialog.onConfirm}
              className={`rounded-2xl px-8 py-6 font-bold font-oswald uppercase tracking-widest text-xs transition-all shadow-xl ${
                confirmDialog.type === 'danger' 
                  ? 'bg-red-600 text-white hover:bg-red-500 shadow-red-900/40' 
                  : 'bg-emerald-600 text-white hover:bg-emerald-500 shadow-emerald-900/40'
              }`}
            >
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
